import { computed, onMounted, onScopeDispose, ref, unref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery, useQueryClient } from '@tanstack/vue-query';

import { useEventHub } from '@satellite/composables/useEventHub';
import { useNotify } from '@satellite/composables/useNotify';
import yardViewService from '@satellite/services/yard-view-service';

import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from './queryKeys';

import type { ComputedRef, Ref } from 'vue';
import type { AssetContainerEventType, Maybe } from '@nova/core';
import type { SpotGridOpenCheckTaskRef, SpotGridOpenMoveTaskRef } from './yard/spotGridTypes';

/** Spot a row is parked on (`YardViewSpotRefDto`); null while the asset waits for assignment. */
export type YardAssetRowSpotRef = {
  id: string;
  code: string;
  spotAreaId?: Maybe<string>;
};

/** Area of the spot a row is parked on (`YardViewSpotAreaRefDto`). */
export type YardAssetRowSpotAreaRef = {
  id: string;
  name: string;
};

/** One row of `GET yard/view/assets` (Neutron `YardViewAssetRowDto`). */
export type YardAssetRow = {
  id: string;
  code: string;
  type?: Maybe<string>;
  /** Latest `AssetContainerEvent` type, resolved server-side. */
  status?: Maybe<AssetContainerEventType>;
  spot?: Maybe<YardAssetRowSpotRef>;
  spotArea?: Maybe<YardAssetRowSpotAreaRef>;
  openMoveTasks?: Maybe<SpotGridOpenMoveTaskRef[]>;
  openCheckTasks?: Maybe<SpotGridOpenCheckTaskRef[]>;
};

/**
 * Topics that add, drop, relocate or unlock a row. Task events are included — unlike the sibling
 * container lists — because the open-task refs ride on every row.
 */
const LIVE_EVENTS = [
  'create-AssetContainer',
  'update-AssetContainer',
  'delete-AssetContainer',
  'create-AssetContainerEvent',
  'update-AssetContainerEvent',
  'delete-AssetContainerEvent',
  'create-SpotAssignment',
  'update-SpotAssignment',
  'delete-SpotAssignment',
  'create-YardTask',
  'update-YardTask',
  'delete-YardTask'
] as const;

export interface UseYardAssetRowsPresenterOptions {
  warehouseId: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;
  /** When true the query runs without viewport gating. Drawers and dialogs want this. */
  headless?: boolean;
  staleTime?: number;
  /**
   * Defaults to `'always'` under {@link headless}, otherwise to TanStack's `true`.
   *
   * A headless consumer is typically torn down with its dialog, taking the EventHub subscription
   * with it. Nothing then marks the cache dirty, and under `staleTime: Infinity` the default
   * `true` never refires — so reopening would replay stale rows within `gcTime`.
   */
  refetchOnMount?: boolean | 'always';
}

/**
 * Selectable asset containers from the `yard/view/assets` BFF, each with its spot and open tasks.
 *
 * One cache entry per warehouse, kept fresh by EventHub invalidation. Task refs come down with the
 * rows, so consumers never issue a second task request.
 */
export function useYardAssetRowsPresenter(options: UseYardAssetRowsPresenterOptions) {
  const {
    warehouseId,
    headless = false,
    staleTime = Infinity,
    refetchOnMount = headless ? 'always' : true
  } = options;
  const { t } = useI18n();
  const { notify } = useNotify();
  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveWarehouseId = computed(() => unref(warehouseId) || null);
  const assetRowsQueryKey = computed(() =>
    effectiveWarehouseId.value ? queryKeys.assetRowsList(effectiveWarehouseId.value) : []
  );

  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);

  const invalidate = () => {
    if (assetRowsQueryKey.value.length) {
      queryClient.invalidateQueries(
        { queryKey: assetRowsQueryKey.value },
        DEFAULT_INVALIDATE_OPTIONS
      );
    }
  };

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    if (headless) {
      return;
    }

    const isIntersecting = typeof entry === 'boolean' ? entry : (entry?.isIntersecting ?? true);
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const assetRowsQuery = useQuery({
    queryKey: assetRowsQueryKey,
    queryFn: async ({ signal }) => {
      const id = effectiveWarehouseId.value;

      if (!id) {
        return [] as YardAssetRow[];
      }

      const response = await yardViewService.getAssets(id, {
        requestConfig: { signal },
        enableCache: false
      });

      const rawAssets = (response as { assets?: unknown })?.assets;
      return (Array.isArray(rawAssets) ? rawAssets : []) as YardAssetRow[];
    },
    enabled: computed(() => Boolean(effectiveWarehouseId.value) && isInViewport.value),
    staleTime,
    refetchOnMount,
    refetchOnWindowFocus: false
  });

  const shouldHandleWarehouseScopedEvent = (event: { warehouseId?: Maybe<string> }) => {
    if (!effectiveWarehouseId.value) {
      return false;
    }

    if (event?.warehouseId && event.warehouseId !== effectiveWarehouseId.value) {
      return false;
    }

    return true;
  };

  const handleWarehouseScopedInvalidation = (event: { warehouseId?: Maybe<string> }) => {
    if (!shouldHandleWarehouseScopedEvent(event)) {
      return;
    }

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  watch(effectiveWarehouseId, () => {
    pendingInvalidation.value = false;
  });

  watch(
    () => assetRowsQuery.error.value,
    newError => {
      if (!newError) {
        return;
      }

      notify(t('yard.messages.failedToGetAssetContainers'), 'error');
    }
  );

  const data = computed<YardAssetRow[]>(() => assetRowsQuery.data.value ?? []);
  const isLoading = computed(() => assetRowsQuery.isLoading.value);
  const isFetching = computed(() => assetRowsQuery.isFetching.value);
  const error = computed(() => assetRowsQuery.error.value);

  const refresh = async () => {
    await assetRowsQuery.refetch();
  };

  onMounted(() => {
    LIVE_EVENTS.forEach(event => eventHub.on(event, handleWarehouseScopedInvalidation));
  });

  onScopeDispose(() => {
    LIVE_EVENTS.forEach(event => eventHub.off(event, handleWarehouseScopedInvalidation));
  });

  return {
    data,

    isLoading,
    isFetching,
    error,

    refresh,
    invalidate,

    onIntersect: handleIntersect,

    query: assetRowsQuery
  };
}
